import { useState, useEffect, useMemo, useRef } from 'react'
import { X, RefreshCw, Clock, CheckCircle2 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { useWorkingOrders } from '../../hooks/useWorkingOrders'
import { useOrderGateway } from '../../hooks/useOrderGateway'
import { useBiometricGate } from '../../hooks/useBiometricGate'
import { useAccountNicknames } from '../../hooks/useAccountNicknames'
import AccountName from '../AccountName'
import PrivateValue from '../PrivateValue'
import OrderConfirmationModal from '../OrderConfirmationModal'
import { classifyOrder, firstTapMatchSet, buildBatchMoveOrders } from '../../lib/workingOrdersSelection.js'
import { buildSplitBracketOrders } from '../../lib/bracketOrderGroups.js'

const fmtCurrency = (val) => {
  if (val == null) return '\u2014'
  return Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const STATUS_LABELS = {
  WORKING: 'Working',
  ACCEPTED: 'Accepted',
  QUEUED: 'Queued',
  AWAITING_PARENT_ORDER: 'Awaiting Trigger',
  AWAITING_CONDITION: 'Awaiting Condition',
  AWAITING_STOP_CONDITION: 'Awaiting Stop',
  PENDING_ACTIVATION: 'Pending',
  NEW: 'New',
  FILLED: 'Filled',
  CANCELED: 'Canceled',
  EXPIRED: 'Expired',
  REJECTED: 'Rejected',
  AWAITING_ENTRY: 'Awaiting Entry',
}

const TERMINAL_STATUSES = new Set(['FILLED', 'CANCELED', 'EXPIRED', 'REJECTED', 'REPLACED'])

function getDisplayStatus(order, conditionalStopKeys) {
  // EF-129: Check if this stop is conditional on an unfilled bracket parent.
  // Must run BEFORE the SELL-side upgrade below, which would otherwise mask
  // it as WORKING and make conditional / live stops look identical.
  if (conditionalStopKeys?.size > 0 && order.stopPrice != null) {
    const instruction = order.side || ''
    if (instruction.includes('SELL')) {
      const sym = order.symbol || ''
      const key = `${sym}|${order.accountNumber}|${Number(order.stopPrice).toFixed(2)}`
      if (conditionalStopKeys.has(key)) return 'AWAITING_ENTRY'
    }
  }

  if (order.promotedFromBracket && !TERMINAL_STATUSES.has(order.status)) {
    return 'WORKING'
  }
  if (order.status === 'AWAITING_PARENT_ORDER' || order.status === 'PENDING_ACTIVATION' || order.status === 'AWAITING_STOP_CONDITION') {
    const instruction = order.side || ''
    if (instruction.includes('SELL')) return 'WORKING'
  }
  return order.status
}

const DEFAULT_RISK_PCT = 0.075

const ACTIVE_STATUSES = new Set([
  'WORKING', 'ACCEPTED', 'QUEUED',
  'AWAITING_PARENT_ORDER', 'AWAITING_CONDITION', 'AWAITING_STOP_CONDITION',
  'PENDING_ACTIVATION', 'NEW',
])

function normalizeAccountNumbers(values) {
  return [...new Set(
    (Array.isArray(values) ? values : [])
      .map(value => (value == null ? '' : String(value).trim()))
      .filter(Boolean),
  )].sort((a, b) => a.localeCompare(b))
}

function orderInstruction(order) {
  return String(
    order?.side
    || order?.instruction
    || order?.orderLegCollection?.[0]?.instruction
    || ''
  ).toUpperCase()
}

function isBuyInstruction(instruction) {
  return instruction === 'BUY' || instruction === 'BUY_TO_OPEN'
}

function isCancelableSetupEntryOrder(order) {
  if (!order?.cancelable) return false
  const status = String(order.status || '').toUpperCase()
  if (!ACTIVE_STATUSES.has(status)) return false
  const directInstruction = orderInstruction(order)
  if (isBuyInstruction(directInstruction)) return true
  const grouped = Array.isArray(order.groupedOrders) ? order.groupedOrders : []
  return grouped.length > 0 && grouped.every(child => isBuyInstruction(orderInstruction(child)))
}

/**
 * Working Orders Panel — shown for WORKING stage positions.
 * Displays live order status from Schwab with quick actions.
 */
export default function WorkingOrdersPanel({
  projects = [],
  trades = [],
  symbol,
  accountNumbers = null,
  onPositionRefresh,
  liveStopPrice = null,
  portfolioValue = 0,
  workingOrdersApi = null,
}) {
  const { user } = useAuth()
  const { getNickname } = useAccountNicknames()
  const biometricGate = useBiometricGate()
  const primary = projects[0] || null

  // EF-189: pipe every tracked orderId from this position's projects into the
  // hook so groupSplitBracketOrders can collapse target + runner pairs into
  // a single SPLIT_BRACKET row. Untracked orders pass through unchanged.
  const trackedOrderIdsForVisiblePositions = useMemo(
    () => projects.flatMap(p => Array.isArray(p?.schwab_order_ids) ? p.schwab_order_ids : []),
    [projects],
  )
  const localWorkingOrdersApi = useWorkingOrders({
    trackedOrderIds: trackedOrderIdsForVisiblePositions,
  })
  const {
    orders,
    fetchOrders,
    refreshForSymbol,
    ready: ordersReady,
    conditionalStopKeys,
  } = workingOrdersApi || localWorkingOrdersApi
  const hasSharedWorkingOrdersApi = !!workingOrdersApi
  const [refreshing, setRefreshing] = useState(false)
  const [totalEquity, setTotalEquity] = useState(0)
  const [accountEquities, setAccountEquities] = useState([])
  // Inline edit state
  const [editMode, setEditMode] = useState(false)
  const [editTrigger, setEditTrigger] = useState('')
  const [editStop, setEditStop] = useState('')
  const [editTarget, setEditTarget] = useState('')
  const [editSizePct, setEditSizePct] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  // Batch stop selection (EF-113) — selectedOrderIds drives the per-row
  // selection circle and the floating action bar. Replaces EF-112's
  // single-order ↕ inline editor; the per-order × cancel button stays.
  // batchMoveOpen swaps the action bar for an inline price input when the
  // user clicks Move on the floating bar.
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set())
  const [batchMoveOpen, setBatchMoveOpen] = useState(false)
  const [batchMovePrice, setBatchMovePrice] = useState('')
  // Tracks whether the pending confirmation is a full cancel-all (should transition to idea)
  const cancelAllFlagRef = useRef(false)

  useEffect(() => {
    if (!user) return
    ;(async () => {
      const { data } = await supabase
        .from('schwab_accounts')
        .select('account_number, nick_name, account_type, equity')
        .eq('user_id', user.id)
      const accts = (data || []).map(a => {
        const eq = a.equity
        const equityVal = typeof eq === 'object' ? (eq.liquidationValue || eq.totalAccountValue || 0) : Number(eq) || 0
        return { account_number: a.account_number, equity: equityVal }
      }).filter(a => a.equity > 0)
      setAccountEquities(accts)
      setTotalEquity(accts.reduce((s, a) => s + a.equity, 0))
    })()
  }, [user])

  // Get all accounts that may own live orders. For active positions,
  // account_sizing can be stale setup metadata; open trades are the safer
  // source for the actual held accounts.
  const derivedAccounts = useMemo(() => {
    const allSizing = projects.flatMap(p => p.account_sizing || [])
    const symbolUpper = symbol?.toUpperCase()
    const liveTradeAccounts = trades
      .filter(t =>
        (!symbolUpper || t.symbol?.toUpperCase() === symbolUpper) &&
        (t.status === 'open' || t.status === 'partial')
      )
      .map(t => t.account_number)
    return normalizeAccountNumbers([
      ...allSizing.map(a => a.account_number),
      ...liveTradeAccounts,
    ])
  }, [projects, trades, symbol])
  const explicitAccounts = useMemo(() => {
    if (!Array.isArray(accountNumbers)) return []
    return normalizeAccountNumbers(accountNumbers)
  }, [accountNumbers])
  const accounts = explicitAccounts.length > 0 ? explicitAccounts : derivedAccounts
  const accountKey = accounts.join(',')

  // Load orders when the symbol or scoped accounts hydrate. This panel may
  // mount while projects/account_sizing are still loading; rerunning on
  // accountKey prevents the user from needing the manual refresh button to
  // see split bracket child orders promoted by refresh-position.
  useEffect(() => {
    if (hasSharedWorkingOrdersApi) return
    if (symbol && accounts.length > 0) {
      refreshForSymbol(symbol, accounts)
    } else if (!symbol) {
      fetchOrders()
    }
  }, [hasSharedWorkingOrdersApi, symbol, accountKey, refreshForSymbol, fetchOrders]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true)
    if (accounts.length > 0) {
      await refreshForSymbol(symbol, accounts)
    } else {
      await fetchOrders()
    }
    setRefreshing(false)
  }

  // Filter orders for this symbol
  const symbolOrders = useMemo(() => {
    if (!orders || !symbol) return []
    return orders.filter(o => o.symbol === symbol.toUpperCase())
  }, [orders, symbol])

  // Group by account
  const ordersByAccount = useMemo(() => {
    const grouped = {}
    for (const order of symbolOrders) {
      const acct = order.accountNumber || 'unknown'
      if (!grouped[acct]) grouped[acct] = []
      grouped[acct].push(order)
    }
    return grouped
  }, [symbolOrders])

  // Count active vs terminal
  const activeCount = symbolOrders.filter(o => ACTIVE_STATUSES.has(o.status)).length
  const filledCount = symbolOrders.filter(o => o.status === 'FILLED').length

  // Order gateway for cancel + send actions
  const { omsEnabled, schwabAuthExpired, confirmState, showConfirmation, confirmOrder, cancelConfirmation } = useOrderGateway({
    biometricGate,
    onOrdersChanged: async (/* results — EF-124, ignored on this side */) => {
      // EF-113: clear stale batch selection — orderIds may no longer exist
      // after a cancel/move/sync.
      setSelectedOrderIds(new Set())
      // Only transition to idea if the user hit "Cancel All Orders"
      if (cancelAllFlagRef.current && primary?.id) {
        cancelAllFlagRef.current = false
        await supabase.from('position_projects').update({
          stage: 'idea',
          setup_status: 'idea',
          updated_at: new Date().toISOString(),
        }).eq('id', primary.id)
      } else {
        cancelAllFlagRef.current = false
      }
      if (onPositionRefresh) onPositionRefresh()
      setTimeout(() => handleRefresh(), 2000)
    },
  })

  // EF-189: a SPLIT_BRACKET row is one logical bracket backed by two Schwab
  // orderIds. Cancel must issue BOTH ids; expand grouped rows here before
  // building the per-order payloads + summaries.
  const expandSplitBrackets = (rows) => {
    const out = []
    for (const row of rows) {
      if (row?.groupKind === 'SPLIT_BRACKET' && Array.isArray(row.groupedOrders)) {
        for (const sibling of row.groupedOrders) out.push(sibling)
        continue
      }
      out.push(row)
    }
    return out
  }

  const buildCancelConfirmation = (ordersToCancel) => {
    const expanded = expandSplitBrackets(ordersToCancel)
    const orderSummaries = expanded.map(o => {
      const side = o.side || ''
      const price = o.price || o.stopPrice || 0
      const isBuy = side === 'BUY'
      const isStop = !!o.stopPrice && !o.price
      const isTarget = side.includes('SELL') && !!o.price && !o.stopPrice

      let orderLabel = 'Order'
      if (isBuy && o.stopPrice) orderLabel = 'Stop Buy'
      else if (isBuy) orderLabel = 'Limit Buy'
      else if (isStop) orderLabel = 'Stop Loss'
      else if (isTarget) orderLabel = 'Take Profit'
      else if (side.includes('SELL')) orderLabel = 'Sell'

      return {
        accountName: getNickname(o.accountNumber) || `****${o.accountNumber?.slice(-4)}`,
        accountNumber: o.accountNumber,
        quantity: o.quantity || o.remainingQuantity || 0,
        side: 'CANCEL',
        orderType: 'CANCEL',
        orderId: o.orderId,
        orderLabel,
        price: Number(price),
      }
    })

    const cancelPayloads = expanded.map(o => ({
      account_number: o.accountNumber,
      symbol: symbol.toUpperCase(),
      orderType: 'CANCEL',
      existingOrderId: o.orderId,
    }))

    showConfirmation('CANCEL', symbol, orderSummaries, cancelPayloads, null)
  }

  const handleCancelAll = () => {
    const cancelableOrders = symbolOrders.filter(o => o.cancelable && ACTIVE_STATUSES.has(o.status))
    if (cancelableOrders.length === 0) return
    cancelAllFlagRef.current = true
    buildCancelConfirmation(cancelableOrders)
  }

  const handleCancelAccount = (acctNum) => {
    const cancelableOrders = symbolOrders.filter(o =>
      o.accountNumber === acctNum && o.cancelable && ACTIVE_STATUSES.has(o.status)
    )
    if (cancelableOrders.length === 0) return
    cancelAllFlagRef.current = false
    buildCancelConfirmation(cancelableOrders)
  }

  // ── Per-order cancel & move (EF-112) ──────────────────────────────────
  // The per-account "Cancel" button still cancels every order on an account,
  // and the Quick Actions "Move Stop" still moves every stop to a uniform
  // price. These handlers give granular control: act on a SINGLE order.

  const handleCancelOrder = (order) => {
    if (!order.cancelable || !ACTIVE_STATUSES.has(order.status)) return
    cancelAllFlagRef.current = false
    buildCancelConfirmation([order])
  }

  // ── Batch stop selection & action handlers (EF-113) ──────────────────
  // The auto-select-by-price first-tap behavior is the headline UX:
  // tapping any one $X stop selects every $X stop across all accounts.
  // After that, individual taps add/remove single orders.

  const clearSelection = () => setSelectedOrderIds(new Set())

  const handleToggleSelectOrder = (order) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev)
      if (next.has(order.orderId)) {
        next.delete(order.orderId)
        return next
      }
      if (prev.size === 0) {
        return firstTapMatchSet(order, symbolOrders, { activeStatuses: ACTIVE_STATUSES })
      }
      next.add(order.orderId)
      return next
    })
  }

  const selectedOrders = useMemo(() => {
    if (selectedOrderIds.size === 0) return []
    return symbolOrders.filter(o => selectedOrderIds.has(o.orderId))
  }, [selectedOrderIds, symbolOrders])

  // EF-187a: derive a single selectionType so UI can branch cleanly on
  // 'buy' | 'stop' | 'tp' | 'mixed' | null. 'mixed' hides the Move button.
  const selectionType = useMemo(() => {
    if (selectedOrders.length === 0) return null
    const types = new Set(selectedOrders.map(classifyOrder))
    if (types.size === 1) return [...types][0]
    return 'mixed'
  }, [selectedOrders])
  // EF-122: when a BUY parent is selected, Schwab cascade-cancels its
  // children. The cascade-cancel ids logic below stays scoped to BUY parents.
  const selectionHasBuy = selectionType === 'buy'

  // EF-122b: cascade indicator via price+account heuristic.
  // Schwab returns each bracket leg as a separate top-level order, and the
  // orderIds extracted from nested childOrderStrategies don't reliably match
  // those top-level rows (OCO wrappers, varying nesting). Match by data
  // instead: the parent's `computedStopPrice` IS the bracket's protective
  // stop level, so any SELL stop in the same account at that exact price
  // is a child Schwab will cascade-cancel. Standalone position stops at
  // a different price are untouched.
  const cascadeCancelIds = useMemo(() => {
    if (selectedOrderIds.size === 0) return new Set()

    const acctBracketPrices = {}
    for (const order of symbolOrders) {
      if (!selectedOrderIds.has(order.orderId)) continue
      const instruction = order.side || ''
      const isBuyOrder = instruction === 'BUY' || instruction === 'BUY_TO_OPEN'
      if (!isBuyOrder) continue
      const bracketStopPrice = order.computedStopPrice
      if (bracketStopPrice == null) continue
      const acct = order.accountNumber
      if (!acctBracketPrices[acct]) acctBracketPrices[acct] = new Set()
      acctBracketPrices[acct].add(Number(bracketStopPrice).toFixed(2))
    }

    const ids = new Set()
    for (const order of symbolOrders) {
      if (selectedOrderIds.has(order.orderId)) continue
      const instruction = order.side || ''
      if (!instruction.includes('SELL')) continue
      const stopPrice = order.stopPrice
      if (!stopPrice) continue
      const prices = acctBracketPrices[order.accountNumber]
      if (!prices) continue
      if (prices.has(Number(stopPrice).toFixed(2))) {
        ids.add(String(order.orderId))
      }
    }
    return ids
  }, [selectedOrderIds, symbolOrders])

  const handleCancelSelected = () => {
    if (selectedOrders.length === 0) return
    cancelAllFlagRef.current = false
    buildCancelConfirmation(selectedOrders)
    clearSelection()
  }

  const handleMoveSelected = () => {
    if (selectedOrders.length === 0) return
    // Pre-fill with the common price when all selected orders agree —
    // first-tap auto-select guarantees this in the common case. TP rows
    // read from `price`; stop rows read from `stopPrice`.
    const priceField = selectionType === 'tp' ? 'price' : 'stopPrice'
    const prices = [...new Set(selectedOrders.map(o => o[priceField]))]
    setBatchMovePrice(prices.length === 1 ? String(prices[0]) : '')
    setBatchMoveOpen(true)
  }

  const handleConfirmBatchMove = () => {
    const price = parseFloat(batchMovePrice)
    if (!price || price <= 0 || selectedOrders.length === 0) return

    const isTP = selectionType === 'tp'
    const orderSummaries = selectedOrders.map(o => ({
      accountName: getNickname(o.accountNumber) || `****${o.accountNumber?.slice(-4)}`,
      accountNumber: o.accountNumber,
      quantity: o.quantity || o.remainingQuantity || 0,
      oldStop: isTP ? null : Number(o.stopPrice),
      newStop: isTP ? null : price,
      oldTarget: isTP ? Number(o.price) : null,
      newTarget: isTP ? price : null,
    }))

    const gatewayOrders = buildBatchMoveOrders({ selectedOrders, newPrice: price, symbol })

    showConfirmation('REPLACE', symbol, orderSummaries, gatewayOrders, null)
    setBatchMoveOpen(false)
    setBatchMovePrice('')
    clearSelection()
  }

  const handleCancelBatchMove = () => {
    setBatchMoveOpen(false)
    setBatchMovePrice('')
  }

  const handleSendAccount = (acct) => {
    const trigger = Number(primary?.trigger_price) || 0
    const stop = Number(primary?.pattern_failure_price) || 0
    const target = Number(primary?.sell_target_price) || 0
    const shares = Number(acct.shares) || 0
    if (!trigger || !stop || shares <= 0) return
    // EF-182 stop-only contract: 1–2-share positions skip the take-profit
    // tranche entirely. Block ≥3-share sends only when the target is missing,
    // since those still need a TP leg by setup convention.
    const useTakeProfit = shares >= 3 && target > 0
    if (shares >= 3 && !useTakeProfit) return

    const sellQty = useTakeProfit ? Math.floor(shares / 3) : 0
    // EF-189: route through buildSplitBracketOrders so partial brackets
    // emit a target tranche + a runner tranche instead of the unsafe
    // nested-TRIGGER replacement-stop shape rejected by the OMS.
    const splitOrders = buildSplitBracketOrders({
      accountNumber: acct.account_number,
      symbol: symbol.toUpperCase(),
      quantity: shares,
      entryPrice: trigger,
      stopPrice: stop,
      targetPrice: useTakeProfit ? target : null,
      targetQuantity: useTakeProfit ? sellQty : 0,
      entryType: primary?.entry_type,
      duration: 'GOOD_TILL_CANCEL',
      session: 'NORMAL',
      assetType: 'EQUITY',
    })
    const splitBracket = splitOrders.length > 1
    const bracketGroupId = splitBracket ? splitOrders[0].bracketGroupId : null

    const orderSummaries = [{
      accountName: getNickname(acct.account_number) || `****${acct.account_number.slice(-4)}`,
      accountNumber: acct.account_number,
      quantity: shares,
      runnerQty: shares - sellQty,
      side: 'BUY',
      orderType: 'LIMIT',
      limitPrice: trigger,
      stopPrice: stop,
      targetPrice: useTakeProfit ? target : null,
      sellQty,
      splitBracket,
      bracketGroupId,
    }]

    cancelAllFlagRef.current = false
    showConfirmation('STAGE_ORDERS', symbol, orderSummaries, splitOrders, null)
  }

  // Recompute per-account sizing from edit inputs (matches SetupStagingPanel logic)
  const editedSizing = useMemo(() => {
    if (!editMode) return []
    const trigger = Number(editTrigger) || 0
    const stop = Number(editStop) || 0
    const alloc = Number(editSizePct) / 100
    if (!trigger || !stop) return accountEquities.map(a => ({ ...a, shares: 0 }))
    const stopDist = Math.abs(trigger - stop)

    return accountEquities.map(a => {
      const perTranche = a.equity * alloc
      const allocShares = Math.floor(perTranche / trigger)
      const maxLoss = perTranche * DEFAULT_RISK_PCT
      const riskShares = stopDist > 0 ? Math.floor(maxLoss / stopDist) : allocShares
      const shares = Math.min(allocShares, riskShares)
      return { ...a, shares }
    })
  }, [editMode, editTrigger, editStop, editSizePct, accountEquities])

  const handleEditClick = () => {
    setEditTrigger(String(Number(primary?.trigger_price) || ''))
    setEditStop(String(Number(primary?.pattern_failure_price) || ''))
    setEditTarget(String(Number(primary?.sell_target_price) || ''))
    setEditSizePct(String(primary?.recommended_size_pct || 9))
    setEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditMode(false)
  }

  const handleSaveAndReplace = async () => {
    setEditSaving(true)
    const newTrigger = Number(editTrigger)
    const newStop = Number(editStop)
    const newTarget = Number(editTarget)
    const newSizePct = Number(editSizePct)

    if (!newTrigger || !newStop || !newTarget) {
      setEditSaving(false)
      return
    }

    // Build full new sizing from edited allocation
    const newAccountSizing = editedSizing.map(a => ({
      account_number: a.account_number,
      shares: a.shares,
    }))

    // Persist new prices + sizing to every project for this symbol+stage
    for (const p of projects) {
      const projectSizing = newAccountSizing.filter(a =>
        (p.account_sizing || []).some(s => s.account_number === a.account_number)
      )
      await supabase.from('position_projects').update({
        trigger_price: newTrigger,
        pattern_failure_price: newStop,
        sell_target_price: newTarget,
        recommended_size_pct: newSizePct,
        account_sizing: projectSizing.length > 0 ? projectSizing : newAccountSizing,
        updated_at: new Date().toISOString(),
      }).eq('id', p.id)
    }

    // Build new bracket orders for accounts with positive shares
    // EF-189: each logical bracket emits a target + runner tranche when
    // 0 < sellQty < shares so the runner stop survives a take-profit fill.
    const bracketOrders = []
    const summaries = []
    for (const acct of editedSizing) {
      if (acct.shares <= 0) continue
      const useTakeProfit = acct.shares >= 3
      const sellQty = useTakeProfit ? Math.floor(acct.shares / 3) : 0
      const splitOrders = buildSplitBracketOrders({
        accountNumber: acct.account_number,
        symbol: symbol.toUpperCase(),
        quantity: acct.shares,
        entryPrice: newTrigger,
        stopPrice: newStop,
        targetPrice: useTakeProfit ? newTarget : null,
        targetQuantity: useTakeProfit ? sellQty : 0,
        entryType: primary?.entry_type,
        duration: 'GOOD_TILL_CANCEL',
        session: 'NORMAL',
        assetType: 'EQUITY',
      })
      bracketOrders.push(...splitOrders)
      const splitBracket = splitOrders.length > 1
      const bracketGroupId = splitBracket ? splitOrders[0].bracketGroupId : null
      summaries.push({
        accountName: getNickname(acct.account_number) || `****${acct.account_number.slice(-4)}`,
        accountNumber: acct.account_number,
        quantity: acct.shares,
        runnerQty: acct.shares - sellQty,
        side: 'BUY',
        orderType: 'BRACKET_ENTRY',
        limitPrice: newTrigger,
        stopPrice: newStop,
        targetPrice: newTarget,
        sellQty,
        splitBracket,
        bracketGroupId,
      })
    }

    const cancelableOrders = symbolOrders.filter(isCancelableSetupEntryOrder)
    cancelAllFlagRef.current = false

    if (cancelableOrders.length > 0) {
      // EF-189 v3 amend: expand any grouped SPLIT_BRACKET wrappers into
      // their underlying target + runner parents BEFORE building cancel
      // payloads, otherwise REPLACE_SETUP would silently leave one Schwab
      // sibling live while placing the new bracket. `buildCancelConfirmation`
      // already does this for the single-action cancel path; `handleSaveAndReplace`
      // builds its own batched payload and must mirror that expansion.
      const seenOrderIds = new Set()
      const expandedCancelable = expandSplitBrackets(cancelableOrders).filter(o => {
        const id = String(o?.orderId ?? '')
        if (!isCancelableSetupEntryOrder(o)) return false
        if (!id || seenOrderIds.has(id)) return false
        seenOrderIds.add(id)
        return true
      })
      // Batched: cancel existing orders first, then place new brackets
      const cancelPayloads = expandedCancelable.map(o => ({
        account_number: o.accountNumber,
        symbol: symbol.toUpperCase(),
        orderType: 'CANCEL',
        existingOrderId: o.orderId,
      }))

      showConfirmation(
        'REPLACE_SETUP',
        symbol,
        summaries,
        null,
        [
          { action: 'CANCEL', orders: cancelPayloads },
          { action: 'PLACE', orders: bracketOrders },
        ]
      )
    } else {
      // No existing orders — just place new ones
      showConfirmation('STAGE_ORDERS', symbol, summaries, bracketOrders, null)
    }

    setEditMode(false)
    setEditSaving(false)
  }

  // Merged account list: union of account_sizing and ordersByAccount
  const mergedAccounts = useMemo(() => {
    const map = new Map()
    const allSizing = projects.flatMap(p => p.account_sizing || [])
    for (const a of allSizing) {
      if (!a.account_number) continue
      if (!map.has(a.account_number)) {
        map.set(a.account_number, { account_number: a.account_number, shares: 0, orders: [] })
      }
      map.get(a.account_number).shares += (Number(a.shares) || 0)
    }
    for (const [acctNum, acctOrders] of Object.entries(ordersByAccount)) {
      if (!map.has(acctNum)) {
        map.set(acctNum, { account_number: acctNum, shares: 0, orders: [] })
      }
      map.get(acctNum).orders = acctOrders
    }
    return [...map.values()]
  }, [projects, ordersByAccount])

  // Setup card values — when an active position has no formal setup persisted
  // (trigger/stop/target all null because the trade was opened externally or
  // the setup was deleted), fall back to live execution data so the user sees
  // real numbers instead of $0.00.
  const setupTrigger = Number(primary?.trigger_price) || 0
  const setupStop = Number(primary?.pattern_failure_price) || 0
  const setupTarget = Number(primary?.sell_target_price) || 0
  const stage = (primary?.stage || '').toLowerCase()
  const setupAllZero = !setupTrigger && !setupStop && !setupTarget
  const useDerived = stage === 'active' && setupAllZero

  const symbolUpper = symbol?.toUpperCase()
  const symbolOpenTrades = useMemo(
    () => trades.filter(t =>
      (t.status === 'open' || t.status === 'partial') &&
      t.symbol?.toUpperCase() === symbolUpper
    ),
    [trades, symbolUpper]
  )

  const derivedShares = symbolOpenTrades.reduce(
    (s, t) => s + (Number(t.quantity_open) || Number(t.quantity_total) || 0),
    0
  )
  const activeTradeAccounts = useMemo(
    () => normalizeAccountNumbers(symbolOpenTrades.map(t => t.account_number)),
    [symbolOpenTrades]
  )
  let weightedEntry = 0
  if (derivedShares > 0) {
    const weighted = symbolOpenTrades.reduce(
      (s, t) => s + (Number(t.average_price) || Number(t.avg_entry_price) || 0) * (Number(t.quantity_open) || Number(t.quantity_total) || 0),
      0
    )
    weightedEntry = weighted / derivedShares
  }

  const triggerPrice = useDerived ? weightedEntry : setupTrigger
  const stopPrice = useDerived ? (Number(liveStopPrice) || 0) : setupStop
  const targetPrice = useDerived ? 0 : setupTarget
  const setupSizingShares = projects.flatMap(p => p.account_sizing || []).reduce((s, a) => s + (Number(a.shares) || 0), 0)
  const useActiveTradeSizing = stage === 'active' && derivedShares > 0
  const totalShares = useActiveTradeSizing ? derivedShares : setupSizingShares
  const summaryAccountCount = useActiveTradeSizing
    ? (activeTradeAccounts.length || accounts.length)
    : accounts.length
  const totalValue = totalShares * triggerPrice
  const riskPerShare = triggerPrice > 0 && stopPrice > 0 ? Math.abs(triggerPrice - stopPrice) : 0
  const totalRisk = totalShares * riskPerShare
  // Without a formal target, R/R is undefined for derived rows.
  const riskReward = !useDerived && riskPerShare > 0 && targetPrice > 0
    ? ((targetPrice - triggerPrice) / riskPerShare).toFixed(1)
    : null
  // Prefer the parent-supplied portfolioValue (totals across all schwab_accounts);
  // fall back to this panel's local equity sum if the prop wasn't passed.
  const portfolioDenominator = portfolioValue > 0 ? portfolioValue : totalEquity
  const portfolioPct = portfolioDenominator > 0 ? ((totalValue / portfolioDenominator) * 100).toFixed(2) : null

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted">
          Working Orders
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-inkMuted hover:text-ink transition-colors p-0.5"
          title="Refresh order status"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Unified section: setup summary + account breakdown */}
      <div className="bg-canvas border border-border rounded-xl p-3 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted/70">
            {primary?.setup_pattern || 'Setup'}
          </div>
          {!editMode && (
            <button
              onClick={handleEditClick}
              className="font-mono text-[8px] px-2 py-0.5 rounded border border-white/[0.06] text-inkMuted/60 hover:text-ink hover:bg-white/[0.03] uppercase tracking-widest transition-colors"
            >
              Edit
            </button>
          )}
        </div>

        {editMode ? (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="font-mono text-[8px] text-inkMuted uppercase mb-0.5">Entry</div>
                <input
                  type="number"
                  step="0.01"
                  value={editTrigger}
                  onChange={e => setEditTrigger(e.target.value)}
                  className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[10px] text-ink focus:outline-none focus:border-ink/40"
                />
              </div>
              <div>
                <div className="font-mono text-[8px] text-inkMuted uppercase mb-0.5">Stop</div>
                <input
                  type="number"
                  step="0.01"
                  value={editStop}
                  onChange={e => setEditStop(e.target.value)}
                  className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[10px] text-ember focus:outline-none focus:border-ink/40"
                />
              </div>
              <div>
                <div className="font-mono text-[8px] text-inkMuted uppercase mb-0.5">Target</div>
                <input
                  type="number"
                  step="0.01"
                  value={editTarget}
                  onChange={e => setEditTarget(e.target.value)}
                  className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[10px] text-forest focus:outline-none focus:border-ink/40"
                />
              </div>
            </div>
            <div>
              <div className="font-mono text-[8px] text-inkMuted uppercase mb-0.5">Size %</div>
              <input
                type="number"
                step="0.5"
                value={editSizePct}
                onChange={e => setEditSizePct(e.target.value)}
                className="w-full bg-canvas2 border border-border rounded px-2 py-1 font-mono text-[10px] text-ink focus:outline-none focus:border-ink/40"
              />
            </div>
            {editedSizing.length > 0 && (
              <div className="font-mono text-[9px] text-inkMuted/70 space-y-0.5">
                <div className="text-[8px] uppercase tracking-widest text-inkMuted mb-0.5">New Sizing</div>
                {editedSizing.map(a => {
                  const oldShares = (primary?.account_sizing || []).find(s => s.account_number === a.account_number)?.shares || 0
                  return (
                    <div key={a.account_number} className="flex items-center justify-between">
                      <span>
                        <AccountName accountNumber={a.account_number} className="font-mono text-[9px]" />
                      </span>
                      <span className="text-inkMuted/60">
                        <PrivateValue>{oldShares} → {a.shares}</PrivateValue>
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleSaveAndReplace}
                disabled={editSaving || !editTrigger || !editStop || !editTarget}
                className="font-mono text-[9px] font-semibold px-3 py-1.5 rounded-lg border border-[#c4953a] text-[#c4953a] bg-[#c4953a]/10 hover:bg-[#c4953a]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {editSaving ? 'Saving...' : 'Save & Replace Orders'}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={editSaving}
                className="font-mono text-[9px] px-3 py-1.5 rounded-lg border border-white/[0.06] text-inkMuted hover:text-ink transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="font-mono text-[8px] text-inkMuted uppercase">
                  Entry{useDerived && <span className="ml-1 text-inkDim normal-case">(live)</span>}
                </div>
                <div className="font-mono text-[10px] text-ink"><PrivateValue>{triggerPrice > 0 ? fmtCurrency(triggerPrice) : '—'}</PrivateValue></div>
              </div>
              <div>
                <div className="font-mono text-[8px] text-inkMuted uppercase">
                  Stop{useDerived && <span className="ml-1 text-inkDim normal-case">(live)</span>}
                </div>
                <div className="font-mono text-[10px] text-ember"><PrivateValue>{stopPrice > 0 ? fmtCurrency(stopPrice) : '—'}</PrivateValue></div>
              </div>
              <div>
                <div className="font-mono text-[8px] text-inkMuted uppercase">Target</div>
                <div className="font-mono text-[10px] text-forest">
                  {useDerived
                    ? <span className="text-inkMuted">—</span>
                    : <PrivateValue>{targetPrice > 0 ? fmtCurrency(targetPrice) : '—'}</PrivateValue>}
                </div>
              </div>
            </div>
            <div className="font-mono text-[9px] text-inkMuted">
              <PrivateValue>{totalShares} shares across {summaryAccountCount} accounts</PrivateValue>
            </div>
            {primary?.setup_source && (
              <div className="font-mono text-[9px] text-inkMuted/60">
                Source: {primary.setup_source}
              </div>
            )}
          </>
        )}
        {/* Risk summary */}
        <div className="border-t border-white/[0.06] pt-1.5 mt-1.5 grid grid-cols-3 gap-2">
          <div>
            <div className="font-mono text-[8px] text-inkMuted uppercase">Max Loss</div>
            <div className="font-mono text-[9px] text-ember/70"><PrivateValue>{fmtCurrency(totalRisk)}</PrivateValue></div>
          </div>
          <div>
            <div className="font-mono text-[8px] text-inkMuted uppercase">Position</div>
            <div className="font-mono text-[9px] text-ink/70">
              <PrivateValue>{portfolioPct ? `${portfolioPct}%` : '—'}</PrivateValue>
            </div>
          </div>
          <div>
            <div className="font-mono text-[8px] text-inkMuted uppercase">R/R</div>
            <div className="font-mono text-[9px] text-ink/70">{riskReward ? `1:${riskReward}` : '—'}</div>
          </div>
        </div>

        {/* Account breakdown */}
        <div className="border-t border-white/[0.06] pt-2 mt-2 space-y-2">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted/60">Account Breakdown</div>
            <div className="flex items-center gap-2">
              {activeCount > 0 && (
                <span className="font-mono text-[9px] text-blue-400/70 flex items-center gap-1">
                  <Clock size={10} /> {activeCount} active
                </span>
              )}
              {filledCount > 0 && (
                <span className="font-mono text-[9px] text-forest flex items-center gap-1">
                  <CheckCircle2 size={10} /> {filledCount} filled
                </span>
              )}
            </div>
          </div>

          {/* EF-113: floating action bar — appears when any stops are selected.
              Auto-fills the price input with the common selected price (when
              uniform), so a tap-tap-confirm flow is the typical case. */}
          {selectedOrderIds.size > 0 && (
            <div className="bg-white/[0.04] border border-[#c4953a]/20 rounded-lg p-2 mb-2">
              {batchMoveOpen ? (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[9px] text-inkMuted">
                    {selectionType === 'tp' ? 'New target:' : 'New stop:'}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    value={batchMovePrice}
                    onChange={(e) => setBatchMovePrice(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleConfirmBatchMove()
                      if (e.key === 'Escape') handleCancelBatchMove()
                    }}
                    autoFocus
                    className="w-24 font-mono text-[10px] bg-white/[0.04] border border-[#c4953a]/30 rounded px-1.5 py-0.5 text-ink outline-none focus:border-[#c4953a]/60"
                  />
                  <button
                    onClick={handleConfirmBatchMove}
                    disabled={!omsEnabled}
                    className="font-mono text-[8px] px-2 py-0.5 rounded bg-[#c4953a]/10 text-[#c4953a] hover:bg-[#c4953a]/20 transition-colors uppercase tracking-widest disabled:opacity-30"
                  >
                    Move {selectedOrders.length}
                  </button>
                  <button
                    onClick={handleCancelBatchMove}
                    className="p-0.5 rounded hover:bg-white/[0.06] text-inkMuted/40 hover:text-inkMuted transition-colors"
                    title="Cancel edit"
                  >
                    <X size={11} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[9px] text-[#c4953a]">
                    {selectedOrders.length}{' '}
                    {selectionType === 'buy' ? 'entry'
                      : selectionType === 'tp' ? 'target'
                      : 'stop'}{selectedOrders.length !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center gap-2">
                    {(selectionType === 'stop' || selectionType === 'tp') && (
                      <button
                        onClick={handleMoveSelected}
                        disabled={!omsEnabled}
                        className="font-mono text-[8px] px-2 py-0.5 rounded border border-[#c4953a]/20 text-[#c4953a] hover:bg-[#c4953a]/10 transition-colors uppercase tracking-widest disabled:opacity-30"
                      >
                        Move
                      </button>
                    )}
                    <button
                      onClick={handleCancelSelected}
                      disabled={!omsEnabled}
                      className="font-mono text-[8px] px-2 py-0.5 rounded border border-ember/20 text-ember hover:bg-ember/10 transition-colors uppercase tracking-widest disabled:opacity-30"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={clearSelection}
                      className="p-0.5 rounded hover:bg-white/[0.06] text-inkMuted/40 hover:text-inkMuted transition-colors"
                      title="Clear selection"
                    >
                      <X size={11} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {mergedAccounts.length === 0 ? (
            <div className="font-mono text-[10px] text-inkMuted text-center py-3">
              {ordersReady ? 'No accounts configured for this setup' : 'Loading orders...'}
            </div>
          ) : (
            <div className="space-y-1">
              {mergedAccounts.map(acct => {
                const hasOrders = acct.orders.length > 0
                const cancelableOrders = acct.orders.filter(o => o.cancelable && ACTIVE_STATUSES.has(o.status))
                const canCancel = cancelableOrders.length > 0
                // EF-182 stop-only: only ≥3-share positions need a take-profit
                // target. 1–2-share rows can send without one.
                const needsTarget = acct.shares >= 3
                const canSend = !hasOrders && triggerPrice > 0 && stopPrice > 0 && acct.shares > 0 && (!needsTarget || targetPrice > 0)

                return (
                  <div key={acct.account_number} className="rounded-lg p-2 bg-white/[0.02]">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-mono text-[9px] text-inkMuted">
                        <AccountName accountNumber={acct.account_number} className="font-mono text-[9px]" />
                      </div>
                      {hasOrders && canCancel && (
                        <button
                          onClick={() => handleCancelAccount(acct.account_number)}
                          disabled={!omsEnabled}
                          className="font-mono text-[8px] px-2 py-0.5 rounded border border-ember/10 text-ember/40 hover:bg-ember/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-widest"
                        >
                          Cancel
                        </button>
                      )}
                      {!hasOrders && canSend && (
                        <button
                          onClick={() => handleSendAccount(acct)}
                          disabled={!omsEnabled}
                          className="font-mono text-[8px] px-2 py-0.5 rounded border border-forest/10 text-forest/40 hover:bg-forest/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed uppercase tracking-widest"
                        >
                          Send
                        </button>
                      )}
                    </div>

                    {hasOrders ? (
                      acct.orders.map((order, i) => {
                        const side = order.side || ''
                        const qty = order.quantity || order.remainingQuantity || ''
                        const price = order.price || order.stopPrice || ''
                        const displayStatus = getDisplayStatus(order, conditionalStopKeys)
                        const isActive = ACTIVE_STATUSES.has(displayStatus)
                        const isBuy = side === 'BUY'
                        const isSell = side.includes('SELL')

                        // Determine descriptive order label
                        const hasStopPrice = !!order.stopPrice
                        const hasLimitPrice = !!order.price
                        let label = side.replace('_TO_OPEN', '').replace('_TO_CLOSE', '')
                        if (isBuy && hasLimitPrice) {
                          label = 'LIMIT BUY'
                        } else if (isBuy && hasStopPrice) {
                          label = 'STOP BUY'
                        } else if (isSell && hasStopPrice && !hasLimitPrice) {
                          label = 'STOP LOSS'
                        } else if (isSell && hasLimitPrice && !hasStopPrice) {
                          label = 'TAKE PROFIT'
                        } else if (isSell && hasStopPrice && hasLimitPrice) {
                          label = 'STOP LIMIT'
                        } else if (isSell) {
                          label = 'SELL'
                        }

                        const isStop = isSell && hasStopPrice && !hasLimitPrice
                        const isTP = isSell && hasLimitPrice && !hasStopPrice
                        const isSelectable = isStop || isBuy || isTP
                        const isSelected = selectedOrderIds.has(order.orderId)
                        // EF-122: bracket children of a selected BUY parent —
                        // Schwab will cascade-cancel them automatically.
                        const isCascadeTarget = cascadeCancelIds.has(String(order.orderId))

                        return (
                          <div
                            key={order.orderId || i}
                            className={`flex items-center justify-between py-1 ${i > 0 ? 'border-t border-white/[0.04]' : ''} ${
                              isCascadeTarget ? 'opacity-40' : ''
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              {/* EF-113/EF-122/EF-187a: selection circle on stop, BUY entry, and TP rows.
                                  First tap auto-selects every matching-price order across all
                                  accounts (stops match by stopPrice; BUYs by entry trigger; TPs by price). */}
                              {isSelectable && order.cancelable && isActive && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleToggleSelectOrder(order) }}
                                  className={`w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? 'border-[#c4953a] bg-[#c4953a]/20'
                                      : 'border-inkMuted/20 hover:border-inkMuted/40'
                                  }`}
                                  title={isSelected ? 'Deselect' : 'Select (auto-picks all matching prices)'}
                                >
                                  {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-[#c4953a]" />}
                                </button>
                              )}
                              <span className={`font-mono text-[9px] font-semibold ${isBuy ? 'text-forest' : isSell ? 'text-ember' : 'text-ink'}`}>
                                {label}
                              </span>
                              {/* EF-189 v3: SPLIT badge for grouped split-bracket rows so
                                  Brandon can see at a glance that the row is one logical
                                  bracket backed by two Schwab orderIds. Deeper protection
                                  annotations (target qty + runner qty inline) are deferred
                                  to a P2 polish slice. */}
                              {(order.orderType === 'SPLIT_BRACKET' || order.groupKind === 'SPLIT_BRACKET') && (
                                <span
                                  className="font-mono text-[8px] tracking-widest uppercase px-1 py-0.5 rounded border border-[#c4953a]/40 text-[#c4953a]/90 bg-[#c4953a]/10"
                                  title="Split bracket — target tranche + runner tranche"
                                >
                                  Split
                                </span>
                              )}
                              {price && (
                                <span className="font-mono text-[10px] text-ink">
                                  {fmtCurrency(Number(price))}
                                </span>
                              )}
                              {qty && (
                                <span className="font-mono text-[9px] text-inkMuted">
                                  {'×'} {qty} sh
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              {/* Per-order × cancel button — kept from EF-112 for quick
                                  single cancels without going through selection. */}
                              {order.cancelable && isActive && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleCancelOrder(order) }}
                                    disabled={!omsEnabled}
                                    className="p-0.5 rounded hover:bg-ember/10 text-inkMuted/40 hover:text-ember transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                    title="Cancel this order"
                                  >
                                    <X size={11} />
                                  </button>
                                </>
                              )}
                              <span className={`font-mono text-[8px] uppercase tracking-wider min-w-[52px] text-right ${
                                isCascadeTarget ? 'text-ember/60'
                                : displayStatus === 'WORKING' ? 'text-blue-400/60'
                                : displayStatus === 'AWAITING_ENTRY' ? 'text-[#c4953a]/60'
                                : isActive ? 'text-inkMuted/70'
                                : displayStatus === 'FILLED' ? 'text-forest/70'
                                : 'text-inkMuted/50'
                              }`}>
                                {isCascadeTarget ? 'AUTO-CANCEL' : (STATUS_LABELS[displayStatus] || displayStatus)}
                              </span>
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <div className="font-mono text-[9px] text-inkMuted/50 py-1">
                        <PrivateValue>{acct.shares} shares · no orders</PrivateValue>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-1.5">
        <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted px-1">Quick Actions</div>
        <button
          onClick={handleCancelAll}
          disabled={!omsEnabled || activeCount === 0}
          className="w-full font-mono text-[10px] font-semibold px-3 py-2 rounded-lg border border-white/[0.06] bg-transparent text-ember/50 hover:bg-ember/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <X size={12} />
          Cancel All Orders
        </button>
      </div>

      {/* OMS status */}
      <div className="font-mono text-[9px] text-right px-1">
        {schwabAuthExpired ? (
          <span className="text-ember/80 uppercase tracking-widest">{'\u25CF'} Schwab auth expired</span>
        ) : omsEnabled ? (
          <span style={{ color: 'rgb(var(--color-forest))' }}>{'\u25CF'} OMS enabled</span>
        ) : (
          <span className="text-inkMuted">{'\u25CB'} OMS disabled</span>
        )}
      </div>

      <OrderConfirmationModal
        open={confirmState.open}
        onClose={cancelConfirmation}
        onConfirm={confirmOrder}
        loading={confirmState.loading}
        action={confirmState.action}
        symbol={confirmState.symbol}
        orders={confirmState.orders}
        results={confirmState.results}
      />
    </div>
  )
}
